import React, { useState } from 'react';
import { 
  Shield, Mail, CheckCircle, AlertCircle, Send
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';

const SecurityTab = () => {
  const { 
    currentUser, 
    sendVerificationEmail
  } = useAuth();

  // États pour email
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  // Envoyer email de vérification
  const handleSendVerificationEmail = async () => {
    setEmailLoading(true);
    setEmailMessage('');

    const result = await sendVerificationEmail();
    
    if (result.success) {
      setEmailMessage({ type: 'success', text: result.message });
    } else {
      setEmailMessage({ type: 'error', text: result.error });
    }

    setEmailLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
          <Shield size={20} />
          Sécurité
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Gérez la sécurité de votre compte
        </p>
      </div>

      {/* Vérification d'email */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-full ${
            currentUser?.emailVerified 
              ? 'bg-green-100 dark:bg-green-900/30' 
              : 'bg-yellow-100 dark:bg-yellow-900/30'
          }`}>
            <Mail 
              className={currentUser?.emailVerified 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-yellow-600 dark:text-yellow-400'
              } 
              size={24} 
            />
          </div>

          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 dark:text-white mb-1">
              Vérification d'email
            </h4>
            
            {currentUser?.emailVerified ? (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <CheckCircle size={16} />
                <span className="text-sm font-medium">Email vérifié</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 mb-2">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">Email non vérifié</span>
              </div>
            )}
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {currentUser?.emailVerified 
                ? 'Votre adresse email a été vérifiée.' 
                : 'Vérifiez votre email pour sécuriser votre compte et accéder à toutes les fonctionnalités.'
              }
            </p>

            {!currentUser?.emailVerified && (
              <Button
                onClick={handleSendVerificationEmail}
                isLoading={emailLoading}
                disabled={emailLoading}
                icon={Send}
                variant="outline"
              >
                Envoyer l'email de vérification
              </Button>
            )}

            {emailMessage && (
              <div className={`mt-4 p-3 rounded-lg ${
                emailMessage.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
              }`}>
                <p className="text-sm">{emailMessage.text}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Informations de session */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-semibold mb-1">Déconnexion automatique</p>
            <p>
              Pour votre sécurité, vous serez automatiquement déconnecté après 30 minutes d'inactivité. 
              Un avertissement s'affichera 2 minutes avant la déconnexion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;